import { useRef } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { BUILDING_PATH, FunnelHeader } from '@hungryroot/funnel';
import {
  ROUTE_QUIZ_BUDGET,
  ROUTE_QUIZ_BUDGET_BENEFITS,
  ROUTE_QUIZ_DINNER,
  ROUTE_QUIZ_LUNCH,
  BudgetBenefits,
  GoalsBenefits,
  SEGMENT_QUIZ_RESULTS_EVENT_NAME,
  getGoalsSegmentData,
  getHouseholdSizeSegmentData,
  ROUTE_QUIZ_HELLO,
  ROUTE_QUIZ_INTRO,
  ROUTE_EATING_HABITS,
  ROUTE_MACRO_NUTRIENT,
  ROUTE_FOOD_QUALITIES,
  ROUTE_FOOD_FORMATS,
  ROUTE_CUISINES,
  ROUTE_FLAVORS,
  ROUTE_MEAT,
  ROUTE_SEAFOOD,
  ROUTE_PLANTS,
  ROUTE_POLARIZING,
  ROUTE_FOOD_PREFERENCES_INTRO,
  ROUTE_BREAKFAST,
  ROUTE_SNACKS,
  ROUTE_SWEETS,
  ROUTE_EATING_HABITS_DIETARY_NEEDS,
  useQuiz,
  QuizContext,
  Screen,
  ProgressBar,

  /* Pages */
  Cuisines,
  Flavors,
  Hello,
  EatingHabits,
  EatingHabitsDietaryNeeds,
  Polarizing,
  Plants,
  Meat,
  Seafood,
  Breakfast,
  BudgetQuestion,
  Dinner,
  FoodFormats,
  FoodQualities,
  Lunch,
  Sweets,
  HelloGoals,
  Snacks,
  MacroNutrient,
  Intro,
  INTRO_SUBCOPY,
  FoodPreferencesIntro,
} from '@hungryroot/quiz';
import {
  ROUTE_QUIZ_GOALS,
  ROUTE_QUIZ_GOALS_BENEFITS,
} from '@hungryroot/routes';
import { segmentAnalytics as analytics } from '@hungryroot/segment';
import { tagGroups, useApiV1TagGroup } from '@hungryroot/tags';
import styles from './Quiz.module.css';

interface Props {
  testid?: string;
  onQuizComplete: () => void;
}

export function Quiz({ onQuizComplete, testid }: Props) {
  const { location } = useHistory();
  // Using deprecated `useApiV1TagGroup` because v2 doesn't work for the `budget` tag group
  const { data: budgetTags } = useApiV1TagGroup({
    tagGroupId: tagGroups.budget,
  });
  const pathname = location.pathname;

  const scrollRef = useRef<HTMLDivElement>(null);

  const useQuizResult = useQuiz({
    onQuizComplete,
    onQuestionSubmit({ quiz }) {
      const { goals, budget } = quiz;
      const householdSizeSegmentData = getHouseholdSizeSegmentData(quiz);
      const goalsSegmentData =
        goals !== null && goals.length > 0
          ? getGoalsSegmentData(goals)
          : undefined;

      switch (true) {
        case pathname === ROUTE_QUIZ_HELLO: {
          analytics.track(
            SEGMENT_QUIZ_RESULTS_EVENT_NAME,
            householdSizeSegmentData
          );
          break;
        }
        case pathname === ROUTE_QUIZ_GOALS: {
          if (goalsSegmentData !== undefined) {
            analytics.track(SEGMENT_QUIZ_RESULTS_EVENT_NAME, goalsSegmentData);
          }
          break;
        }
        case pathname === ROUTE_QUIZ_BUDGET: {
          if (
            budget !== undefined &&
            budget !== null &&
            budgetTags !== undefined &&
            budgetTags?.tags?.length > 0
          ) {
            const tag = budgetTags.tags[0].find((tag) => tag.id === budget);
            // Ensure we found a valid tag object based on the tag.id (aka `budget` in this case)
            if (tag?.name) {
              analytics.track(SEGMENT_QUIZ_RESULTS_EVENT_NAME, {
                budget: tag.name,
              });
            }
          }
          break;
        }
      }
    },
  });

  const { quiz } = useQuizResult;
  if (quiz === null) {
    const { redirectPath } = useQuizResult;
    if (redirectPath) {
      return <Redirect to={redirectPath} />;
    }
    return null;
  }

  const { sections, onSaveAndExit } = useQuizResult;

  return (
    <QuizContext.Provider value={useQuizResult}>
      <Switch>
        <Route path={ROUTE_QUIZ_INTRO}>
          <Intro
            header={
              <FunnelHeader
                saveAndExitButtonText="Exit"
                onSaveAndExitClick={() => {
                  onSaveAndExit(quiz);
                }}
                className={styles.header}
                centerElement={undefined}
              />
            }
            subcopy={INTRO_SUBCOPY.DEFAULT}
            testid={`${testid}-page-intro`}
          />
        </Route>
        <Route path={ROUTE_QUIZ_BUDGET_BENEFITS}>
          <BudgetBenefits
            testid={`${testid}-page-budget-benefits`}
            next={useQuizResult.navigationState.next}
            prev={useQuizResult.navigationState.prev}
            scrollRef={scrollRef}
          />
        </Route>
        <Route path={ROUTE_QUIZ_GOALS_BENEFITS}>
          <GoalsBenefits
            next={useQuizResult.navigationState.next}
            prev={useQuizResult.navigationState.prev}
            quiz={quiz}
            scrollRef={scrollRef}
          />
        </Route>
        <Route path={ROUTE_FOOD_PREFERENCES_INTRO}>
          <FoodPreferencesIntro
            testid={`${testid}-page-food-preferences-intro`}
            next={useQuizResult.navigationState.next}
            prev={useQuizResult.navigationState.prev}
          />
        </Route>
        <Screen
          scrollRef={scrollRef}
          header={
            <Route
              render={({ location }) =>
                ![BUILDING_PATH].includes(location.pathname) ? (
                  <>
                    <FunnelHeader
                      saveAndExitButtonText="Save and exit"
                      onSaveAndExitClick={() => {
                        onSaveAndExit(quiz);
                      }}
                    ></FunnelHeader>
                    <ProgressBar
                      testid="progress-bar"
                      currentPathName={pathname}
                      sections={sections}
                    />
                  </>
                ) : null
              }
            />
          }
        >
          <Route exact path={ROUTE_QUIZ_HELLO}>
            <Hello testid={`${testid}-page-hello`} />
          </Route>
          <Route path={ROUTE_QUIZ_GOALS}>
            <HelloGoals testid={`${testid}-page-hello-goals`} />
          </Route>
          <Route path={ROUTE_QUIZ_BUDGET}>
            <BudgetQuestion testid={`${testid}-page-budget`} quiz={quiz} />
          </Route>
          <Route exact path={ROUTE_EATING_HABITS}>
            <EatingHabits
              testid={`${testid}-page-eating-habits`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_EATING_HABITS_DIETARY_NEEDS}>
            <EatingHabitsDietaryNeeds
              testid={`${testid}-page-eating-habits-dietary-needs`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_MACRO_NUTRIENT}>
            <MacroNutrient
              testid={`${testid}-page-macro-nutrient`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_FOOD_QUALITIES}>
            <FoodQualities
              testid={`${testid}-page-food-qualities`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_FOOD_FORMATS}>
            <FoodFormats
              testid={`${testid}-page-food-qualities`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_CUISINES}>
            <Cuisines
              testid={`${testid}-page-cuisines`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_FLAVORS}>
            <Flavors testid={`${testid}-page-flavors`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_MEAT}>
            <Meat testid={`${testid}-page-meat`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_SEAFOOD}>
            <Seafood testid={`${testid}-page-seafood`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_PLANTS}>
            <Plants testid={`${testid}-page-plants`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_POLARIZING}>
            <Polarizing
              testid={`${testid}-page-polarizing`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_BREAKFAST}>
            <Breakfast
              testid={`${testid}-page-breakfast`}
              scrollRef={scrollRef}
            />
          </Route>
          <Route path={ROUTE_QUIZ_LUNCH}>
            <Lunch testid={`${testid}-page-lunch`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_QUIZ_DINNER}>
            <Dinner testid={`${testid}-page-dinner`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_SNACKS}>
            <Snacks testid={`${testid}-page-snacks`} scrollRef={scrollRef} />
          </Route>
          <Route path={ROUTE_SWEETS}>
            <Sweets testid={`${testid}-page-sweets`} scrollRef={scrollRef} />
          </Route>
          {useQuizResult.screens.map((screen) => screen)}
        </Screen>
      </Switch>
    </QuizContext.Provider>
  );
}
